import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import reportAPI from "../../services/reportAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { useAuthStore } from "../../stores/authStore";
import { indiaFyMeta } from "../../services/financialYearAPI";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const fmtMins = (m: number) => {
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  return r > 0 ? `${h}h ${r}m` : `${h}h`;
};

const InventoryAlertsPage: React.FC = () => {
  const { user } = useAuthStore();
  const role = String(user?.role || "").toUpperCase();
  const isAdmin = role === "ADMIN" || role === "SUPER_ADMIN" || role === "SUPERADMIN";

  const fy = indiaFyMeta();
  const [tab, setTab] = useState(0);
  const [branchId, setBranchId] = useState(isAdmin ? user?.branchId || "" : "");
  const [salesPointId, setSalesPointId] = useState(user?.salesPointId || "");
  const [alertType, setAlertType] = useState<"all" | "low_stock" | "out_of_stock">("all");
  const [startDate, setStartDate] = useState(fy.startDate);
  const [endDate, setEndDate] = useState(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    return today < fy.endDate ? today : fy.endDate;
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branchesAll"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["salesPointsAll"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const filteredSalesPoints = useMemo(() => {
    if (!branchId) return salesPoints;
    return salesPoints.filter((s) => s.branchId === branchId);
  }, [salesPoints, branchId]);

  const {
    data: lowStock,
    isLoading: loadingLow,
    error: lowError,
  } = useQuery({
    queryKey: ["low-stock-report", branchId, salesPointId, alertType],
    queryFn: () =>
      reportAPI.getLowStockReport({
        branchId: branchId || undefined,
        salesPointId: salesPointId || undefined,
        alertType,
      }),
    enabled: tab === 0,
  });

  const {
    data: turnaround,
    isLoading: loadingTurn,
    error: turnError,
  } = useQuery({
    queryKey: ["table-turnaround", startDate, endDate, branchId, salesPointId],
    queryFn: () =>
      reportAPI.getTableTurnaround({
        startDate,
        endDate,
        branchId: branchId || undefined,
        salesPointId: salesPointId || undefined,
      }),
    enabled: tab === 1,
  });

  return (
    <Box>
      <PageHeader
        title="Inventory alerts"
        subtitle="Reorder / low stock and restaurant table turnaround"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Reports", to: "/reports" },
          { label: "Inventory alerts" },
        ]}
      />

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Branch</InputLabel>
              <Select
                label="Branch"
                value={branchId}
                onChange={(e) => {
                  setBranchId(e.target.value);
                  setSalesPointId("");
                }}
              >
                <MenuItem value="">All</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sales point</InputLabel>
            <Select
              label="Sales point"
              value={salesPointId}
              onChange={(e) => setSalesPointId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {filteredSalesPoints.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {tab === 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Alert type</InputLabel>
              <Select
                label="Alert type"
                value={alertType}
                onChange={(e) =>
                  setAlertType(e.target.value as typeof alertType)
                }
              >
                <MenuItem value="all">All alerts</MenuItem>
                <MenuItem value="out_of_stock">Out of stock</MenuItem>
                <MenuItem value="low_stock">Low stock</MenuItem>
              </Select>
            </FormControl>
          )}
          {tab === 1 && (
            <>
              <TextField
                label="From"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </>
          )}
        </Stack>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Low stock / reorder" />
        <Tab label="Table turnaround" />
      </Tabs>

      {tab === 0 && (
        <>
          {lowError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(lowError as Error).message || "Failed to load low stock report"}
            </Alert>
          )}
          {loadingLow ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip label={`Total: ${lowStock?.summary?.totalAlerts ?? 0}`} />
                <Chip
                  color="error"
                  label={`Out: ${lowStock?.summary?.outOfStock ?? 0}`}
                />
                <Chip
                  color="warning"
                  label={`Low: ${lowStock?.summary?.lowStock ?? 0}`}
                />
                <Chip
                  label={`Suggested reorder qty: ${lowStock?.summary?.suggestedReorderQty ?? 0}`}
                />
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Available</TableCell>
                      <TableCell align="right">Min / reorder</TableCell>
                      <TableCell align="right">Shortfall</TableCell>
                      <TableCell>Batch / expiry</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(lowStock?.items || []).map((row: any) => (
                      <TableRow key={row.inventoryId}>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {row.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.sku}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.branchName}
                          {row.salesPointName ? ` · ${row.salesPointName}` : ""}
                        </TableCell>
                        <TableCell align="right">
                          {row.availableQuantity} {row.unit}
                        </TableCell>
                        <TableCell align="right">{row.minLevel}</TableCell>
                        <TableCell align="right">{row.shortfall}</TableCell>
                        <TableCell>
                          {row.batchNo || "—"}
                          {row.expiryDate ? ` · ${row.expiryDate}` : ""}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={
                              row.alertType === "out_of_stock"
                                ? "error"
                                : "warning"
                            }
                            label={
                              row.alertType === "out_of_stock"
                                ? "Out"
                                : "Low"
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {(lowStock?.items?.length ?? 0) === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" py={3}>
                            No reorder alerts for this scope.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {turnError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(turnError as Error).message ||
                "Failed to load table turnaround"}
            </Alert>
          )}
          {loadingTurn ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip
                  label={`Dine-in bills: ${turnaround?.summary?.completedDineInSales ?? 0}`}
                />
                <Chip
                  color="primary"
                  label={`Avg: ${fmtMins(turnaround?.summary?.avgTurnaroundMinutes ?? 0)}`}
                />
                <Chip
                  label={`Median: ${fmtMins(turnaround?.summary?.medianTurnaroundMinutes ?? 0)}`}
                />
                <Chip
                  label={`Range: ${fmtMins(turnaround?.summary?.fastestMinutes ?? 0)} – ${fmtMins(turnaround?.summary?.slowestMinutes ?? 0)}`}
                />
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Table</TableCell>
                      <TableCell>Sale</TableCell>
                      <TableCell>Opened</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell align="right">Turnaround</TableCell>
                      <TableCell align="right">Bill</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(turnaround?.rows || []).map((row: any) => (
                      <TableRow key={row.saleId}>
                        <TableCell>
                          {row.tableName || "—"}
                          {row.tableSection ? ` (${row.tableSection})` : ""}
                        </TableCell>
                        <TableCell>{row.saleNumber}</TableCell>
                        <TableCell>
                          {format(new Date(row.openedAt), "dd MMM HH:mm")}
                        </TableCell>
                        <TableCell>
                          {format(new Date(row.completedAt), "dd MMM HH:mm")}
                        </TableCell>
                        <TableCell align="right">
                          {fmtMins(row.turnaroundMinutes)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(row.netAmount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(turnaround?.rows?.length ?? 0) === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" py={3}>
                            No completed dine-in sales in this period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default InventoryAlertsPage;
